"use strict";

import NodeBase from "../util/NodeBase";
import { drawRoundRect } from "../util/shapes";

/**
 * A ModelUnit Node/Cluster shape.
 *
 * @augments ModelUnit
 */
class ModelUnit extends NodeBase {
  /**
   * @param {object} options
   * @param {object} body
   * @param {Label} labelModule
   * @param captionModule
   * @param iconsList
   */
  constructor(options, body, labelModule, captionModule, notationModule, iconsList) {
    super(options, body, labelModule, captionModule, notationModule);
    this._setMargins(labelModule);
    this.captionTextSize = { width: 0, height: 0 };
    this.iconsList = iconsList;
  }

  /**
   *
   * @param {CanvasRenderingContext2D} ctx
   * @param {boolean} [selected]
   * @param {boolean} [hover]
   */
  resize(ctx, selected = this.selected, hover = this.hover) {
    if (this.needsRefresh(selected, hover)) {
      this.getDimensionsFromLabel(ctx, selected, hover);
      this.captionTextSize = this.captionModule.getTextSize(
        ctx,
        selected,
        hover
      );

      this.width = 120; //dimensions.width + this.margin.right + this.margin.left;
      this.height = 80; //dimensions.height + this.margin.top + this.margin.bottom;
      this.radius = 0; //this.width / 2;
    }
  }

  /**
   *
   * @param {CanvasRenderingContext2D} ctx
   * @param {boolean} selected
   * @param {boolean} hover
   */
   drawNotation(ctx, selected, hover) {
    if (this.options.notation.label) {
      let ls = this.left + 4;
      let ts = this.top + this.height + 4;
      this.notationModule.draw(ctx, ls, ts, selected, hover, "top", "left");
    }
  }

  /**
   * @param {CanvasRenderingContext2D} ctx
   * @param {number} x width
   * @param {number} y height
   * @param {boolean} selected
   * @param {boolean} hover
   * @param {ArrowOptions} values
   * @param pointer
   */
  draw(ctx, x, y, selected, hover, values, pointer) {
    this.resize(ctx, selected, hover);
    this.left = x - this.width / 2;
    this.top = y - this.height / 2;

    this.initContextForDraw(ctx, values);
    drawRoundRect(
      ctx,
      this.left,
      this.top,
      this.width,
      this.height,
      values.borderRadius
    );
    this.performFill(ctx, values);

    if (this.options.caption.enabled) {
      this.drawCaption(ctx, selected, hover);
    }

    if (this.options.notation.enabled) {
      this.drawNotation(ctx, selected, hover);
    }

    this.updateBoundingBox(x, y, ctx, selected, hover);
    this.drawLabel(ctx, selected, hover);
    if (values.useToolBar) {
      this.drawToolBar(ctx, pointer, selected, hover);
    }
  }

  /**
   *
   * @param {CanvasRenderingContext2D} ctx
   * @param {boolean} selected
   * @param {boolean} hover
   */
  drawLabel(ctx, selected, hover) {
    let ls = this.left + this.textSize.width / 2 + this.margin.left;
    if (
      this.options.align.h &&
      this.width - this.margin.left - this.margin.right >= this.textSize.width
    ) {
      ls =
        this.left +
        this.margin.left +
        (this.width - this.margin.left - this.margin.right) / 2; // - (this.textSize.width / 2);
    }
    let ts =
      this.top +
      this.textSize.height / 2 +
      this.margin.top +
      (this.options.caption.enabled
        ? this.options.caption.height + this.options.caption.margin.top
        : 0);
    if (this.options.align.v && this.height >= this.textSize.height) {
      const capH = this.options.caption.enabled
        ? this.options.caption.height +
          this.options.caption.margin.top +
          this.options.caption.margin.bottom
        : 0;
      ts =
        this.top + capH + (this.height - capH) / 2 - this.textSize.height / 2;
    }
    this.labelModule.draw(ctx, ls, ts, selected, hover);
  }

  /**
   *
   * @param {CanvasRenderingContext2D} ctx
   * @param {boolean} selected
   * @param {boolean} hover
   */
  drawCaption(ctx, selected, hover) {
    ctx.save();
    const height =
      this.options.caption.height > this.captionTextSize.height
        ? this.options.caption.height
        : this.captionTextSize.height;
    ctx.fillStyle = this.options.caption.color.background;
    ctx.fillRect(
      this.left + this.options.caption.margin.left,
      this.top + this.options.caption.margin.top,
      this.width -
        this.options.caption.margin.left -
        this.options.caption.margin.right,
      height
    );
    if (this.options.caption.label) {
      this.captionModule.draw(
        ctx,
        this.left +
          this.captionTextSize.width / 2 +
          this.options.caption.margin.left +
          3,
        this.top + height / 2 + this.options.caption.margin.top,
        selected,
        hover
      );
    }

    ctx.restore();
  }

  /**
   * @param {CanvasRenderingContext2D} ctx
   * @param pointer
   * @param {boolean} selected
   * @param {boolean} hover
   */
  drawToolBar(ctx, pointer, selected, hover) {
    if (!selected) {
      return;
    }
    const lh =
      this.left +
      this.width -
      (2 + 12) * (this.options.subType === "user" ? 3 : 2);
    const th = this.top + this.height - 12 - 4;
    const atItem = this.hitActionItem(pointer);
    if (atItem === "setting" && hover) {
      ctx.drawImage(this.iconsList.setting.hover, lh, th, 12, 12);
    } else {
      ctx.drawImage(this.iconsList.setting.normal, lh, th, 12, 12);
    }
    if (atItem === "link" && hover) {
      ctx.drawImage(this.iconsList.link.hover, lh + 14, th, 12, 12);
    } else {
      ctx.drawImage(this.iconsList.link.normal, lh + 14, th, 12, 12);
    }
    if (this.options.subType === "user") {
      if (atItem === "remove" && hover) {
        ctx.drawImage(this.iconsList.remove.hover, lh + 28, th, 12, 12);
      } else {
        ctx.drawImage(this.iconsList.remove.normal, lh + 28, th, 12, 12);
      }
    }
  }

  /**
   *
   * @param {number} x width
   * @param {number} y height
   * @param {CanvasRenderingContext2D} ctx
   * @param {boolean} selected
   * @param {boolean} hover
   */
  updateBoundingBox(x, y, ctx, selected, hover) {
    this._updateBoundingBox(x, y, ctx, selected, hover);

    const borderRadius = this.options.shapeProperties.borderRadius; // only effective for box
    this._addBoundingBoxMargin(borderRadius);
  }

  /**
   *
   * @param {CanvasRenderingContext2D} ctx
   * @param {number} angle
   * @returns {number}
   */
  distanceToBorder(ctx, angle) {
    if (ctx) {
      this.resize(ctx);
    }
    const borderWidth = this.options.borderWidth;

    return (
      Math.min(
        Math.abs(this.width / 2 / Math.cos(angle)),
        Math.abs(this.height / 2 / Math.sin(angle))
      ) + borderWidth
    );
  }

  /**
   * @param {x: number, y: number} pointer
   */
  hitActionItem(pointer) {
    const lh = this.left + this.width - (2 + 12) * (this.options.subType === "user" ? 3 : 2);
    const rh = this.right - 1;
    const th = this.top + this.height - 12 - 4;
    const bh = this.top + this.height - 2;
    if (pointer.y > bh || pointer.y < th || pointer.x < lh || pointer.x > rh) {
      return "";
    }
    if (pointer.x < lh + 12 + 1) {
      return "setting";
    }
    if (pointer.x < lh + 12 + 2 + 12 + 1) {
      return "link";
    }
    if (this.options.subType === "user") {
      return "remove";
    }
    return "";
  }

  /**
   * @param {x: number, y: number} pointer
   */
  overActionItemName(pointer) {
    return this.hitActionItem(pointer);
  }
}

export default ModelUnit;
